import { useMemo, useState } from "react";
import { usePrava } from "@/api/hooks/useAuth";
import { VETA, smieCennik } from "@/lib/prava";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  useMarzeKategorii,
  useUlozMarzeKategorii,
  type KategoriaMarzeItem,
  type MarzeKategoriiPayload,
  type PoleMarze,
} from "@/api/hooks/useCennik";
import { formatDate, formatPocet, normalizeNum, sklonuj } from "@/lib/format";
import {
  JEDNOTKA_PRACE,
  PREDVOLBA,
  blokyKarty,
  kartyMarzi,
  nazovKategorie,
  type KartaMarzi,
} from "@/components/cennik/marzeKarty";
import {
  KRATKY_NAZOV,
  POLOZIEK,
  formatHodnota,
  formatRozsah,
  nazovPola,
  overHodnotu,
  popisUlozenia,
} from "@/components/cennik/marzeFormat";
import { MarzeSekciaDialog } from "@/components/cennik/MarzeSekciaDialog";

const POLOZKY_NOM = ["položka", "položky", "položiek"] as const;

/** Kľúč riadku — kategória v jednom druhu (KOVANIE_PROFIL je dvakrát). */
function kluc(p: KategoriaMarzeItem): string {
  return `${p.druh}:${p.kategoria}`;
}

function par(p: KategoriaMarzeItem): MarzeKategoriiPayload["polozky"][number] {
  return { druh: p.druh, kategoria: p.kategoria };
}

const CHIP =
  "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] leading-4";

/**
 * Nastavenia cien (KOLO 166) — práca, réžia a nadmiera každej kategórie
 * v appke (`kategoria_marze`). Čo sa tu uloží, berie import cenníka aj
 * nová položka, takže to prežije deploy.
 *
 * Karty podľa sekcie zo servera a jednotky práce, kovanie po skupinách
 * (`item.skupina`). Klik na číslo → úprava, Enter uloží (len to jedno
 * pole, len tej kategórii alebo skupine), Esc zruší. Položky s vlastnými
 * maržami sa nemenia — štítok ich počíta a klikom sa dajú prepísať.
 */
export function MarzeKategoriiPanel(): JSX.Element {
  // Marže tovaru mení obchodník, ostatné rozpočtár (KOLO 231)
  const prava = usePrava();
  const query = useMarzeKategorii();
  const ulozit = useUlozMarzeKategorii();
  // "<pole>|<kľúč riadku alebo skupiny>"
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [otvorene, setOtvorene] = useState<Record<string, boolean>>({});
  const [sekcia, setSekcia] = useState<KartaMarzi | null>(null);

  const karty = useMemo(() => kartyMarzi(query.data?.data ?? []), [query.data]);

  function uloz(payload: MarzeKategoriiPayload, nazov: string): void {
    ulozit.mutate(payload, {
      onSuccess: (r) =>
        toast({ title: "Marže uložené", description: popisUlozenia(nazov, r.data) }),
      onError: (err) =>
        toast({
          variant: "destructive",
          title: "Chyba pri ukladaní marží",
          description: extractErrorMessage(err),
        }),
    });
  }

  /** Uloženie bunky — jedno pole, jedna kategória alebo celá skupina. */
  function ulozBunku(
    polozky: KategoriaMarzeItem[],
    pole: PoleMarze,
    nazov: string,
  ): void {
    setEditing(null);
    const hodnota = overHodnotu(pole, editValue);
    if (hodnota === null) {
      toast({
        variant: "destructive",
        title: "Neplatné číslo",
        description: `${KRATKY_NAZOV[pole]}: zadaj nezáporné číslo${
          pole === "praca_eur_per_mj" ? "" : " do 100"
        }.`,
      });
      return;
    }
    // Bez zmeny nič neposielaj — Esc a klik vedľa nemajú nič uložiť
    if (polozky.every((p) => p[pole] != null && Number(p[pole]) === Number(hodnota))) {
      return;
    }
    uloz({ polozky: polozky.map(par), [pole]: hodnota }, nazov);
  }

  /** Čísla kategórie, ktorými sa prepíšu / zjednotia položky. */
  function cislaKategorie(
    p: KategoriaMarzeItem,
    karta: KartaMarzi,
  ): Partial<Record<PoleMarze, string>> {
    const vysledok: Partial<Record<PoleMarze, string>> = {};
    for (const pole of karta.polia) {
      const v = p[pole];
      if (v != null) vysledok[pole] = normalizeNum(v);
    }
    return vysledok;
  }

  function zhrnutie(p: KategoriaMarzeItem, karta: KartaMarzi): string {
    return karta.polia
      .filter((pole) => p[pole] != null)
      .map((pole) => `${KRATKY_NAZOV[pole].toLowerCase()} ${formatHodnota(pole, p[pole])}`)
      .join(", ");
  }

  function prepisVlastne(p: KategoriaMarzeItem, karta: KartaMarzi): void {
    const n = p.pocet_vlastnych;
    const nazov = nazovKategorie(p.kategoria);
    if (
      !window.confirm(
        `Prepísať marže ${formatPocet(n)} ${sklonuj(n, POLOZIEK)} s vlastnými maržami ` +
          `v kategórii „${nazov}"? Dostanú čísla kategórie (${zhrnutie(p, karta)}).`,
      )
    ) {
      return;
    }
    uloz({ polozky: [par(p)], ...cislaKategorie(p, karta), aj_vlastne: true }, nazov);
  }

  function zjednotOdlisne(p: KategoriaMarzeItem, karta: KartaMarzi): void {
    const n = p.pocet_odlisnych;
    const nazov = nazovKategorie(p.kategoria);
    if (
      !window.confirm(
        `Zjednotiť ${formatPocet(n)} ${sklonuj(n, POLOZIEK)} kategórie „${nazov}" ` +
          `s jej číslami (${zhrnutie(p, karta)})?`,
      )
    ) {
      return;
    }
    uloz({ polozky: [par(p)], ...cislaKategorie(p, karta) }, nazov);
  }

  /** Bunka s číslom — tlačidlo, po kliku pole na úpravu. */
  function bunka(
    editKey: string,
    pole: PoleMarze,
    polozky: KategoriaMarzeItem[],
    nazov: string,
  ): JSX.Element {
    const hodnoty = polozky.map((p) => p[pole]);
    if (editing === editKey) {
      return (
        <input
          autoFocus
          type="text"
          inputMode="decimal"
          aria-label={`${KRATKY_NAZOV[pole]} ${nazov}`}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onFocus={(e) => e.target.select()}
          onKeyDown={(e) => {
            if (e.key === "Enter") ulozBunku(polozky, pole, nazov);
            if (e.key === "Escape") setEditing(null);
          }}
          onBlur={() => setEditing(null)}
          className="w-24 border rounded-md px-2 py-1 text-right font-mono text-sm"
        />
      );
    }
    return (
      <button
        type="button"
        disabled={ulozit.isPending}
        onClick={() => {
          setEditing(editKey);
          setEditValue(normalizeNum(hodnoty.find((v) => v != null) ?? ""));
        }}
        title={`Upraviť: ${KRATKY_NAZOV[pole].toLowerCase()} — ${nazov}`}
        className="font-mono text-sm font-medium text-primary hover:underline disabled:opacity-50"
      >
        {polozky.length === 1 ? formatHodnota(pole, hodnoty[0]) : formatRozsah(pole, hodnoty)}
      </button>
    );
  }

  /** Štítky riadku: odkiaľ číslo je, vlastné a odlišné položky. */
  function stitky(p: KategoriaMarzeItem, karta: KartaMarzi): JSX.Element {
    const inaJednotka = JEDNOTKA_PRACE[p.druh];
    return (
      <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
        {p.kategoria === PREDVOLBA ? (
          <span>pre kategórie bez vlastného čísla</span>
        ) : (
          <span>
            {formatPocet(p.pocet)} {sklonuj(p.pocet, POLOZKY_NOM)}
            {inaJednotka && inaJednotka !== karta.jednotka ? ` · ${inaJednotka}` : ""}
          </span>
        )}
        {p.zdroj === "predvolba" || p.zdroj === "yaml" ? (
          <span
            className={CHIP}
            title="Kategória ešte nemá číslo v appke — import berie predvoľbu. Uloží sa, keď ho zmeníš."
          >
            predvolené
          </span>
        ) : null}
        {p.zdroj === "rucne" && (p.autor || p.upravene_at) ? (
          <span title="Naposledy upravené v Nastaveniach cien">
            · upravil {p.autor ?? "?"} {formatDate(p.upravene_at)}
          </span>
        ) : null}
        {p.pocet_vlastnych > 0 ? (
          <button
            type="button"
            disabled={ulozit.isPending}
            onClick={() => prepisVlastne(p, karta)}
            className={`${CHIP} border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50`}
            title="Import ani Nastavenia cien ich nemenia. Klik — prepísať číslami kategórie."
          >
            {formatPocet(p.pocet_vlastnych)} s vlastnými maržami
          </button>
        ) : null}
        {p.pocet_odlisnych > 0 ? (
          <button
            type="button"
            disabled={ulozit.isPending}
            onClick={() => zjednotOdlisne(p, karta)}
            className={`${CHIP} hover:bg-accent disabled:opacity-50`}
            title="Majú iné čísla než kategória (vlastné marže nemajú). Zjednotí ich ďalší import cenníka; klik — zjednotiť hneď."
          >
            {formatPocet(p.pocet_odlisnych)} odlišných
          </button>
        ) : null}
      </div>
    );
  }

  function riadok(p: KategoriaMarzeItem, karta: KartaMarzi, odsadeny = false): JSX.Element {
    const nazov = nazovKategorie(p.kategoria);
    return (
      <tr key={kluc(p)} className="border-t hover:bg-accent/40">
        <td className={`py-1.5 pr-2 ${odsadeny ? "pl-8" : "pl-2"}`}>
          <div className="text-sm font-medium">{nazov}</div>
          {stitky(p, karta)}
        </td>
        {karta.polia.map((pole) => (
          <td key={pole} className="py-1.5 px-2 text-right align-top">
            {bunka(`${pole}|${kluc(p)}`, pole, [p], nazov)}
          </td>
        ))}
      </tr>
    );
  }

  function skupina(kod: string, clenovia: KategoriaMarzeItem[], karta: KartaMarzi): JSX.Element[] {
    const otvorena = !!otvorene[`${karta.kluc}:${kod}`];
    const nazov = nazovKategorie(kod);
    const pocet = clenovia.reduce((s, p) => s + p.pocet, 0);
    const vlastnych = clenovia.reduce((s, p) => s + p.pocet_vlastnych, 0);
    const riadky = [
      <tr key={`grp:${kod}`} className="border-t hover:bg-accent/40">
        <td className="py-1.5 px-2">
          <button
            type="button"
            onClick={() =>
              setOtvorene((o) => ({ ...o, [`${karta.kluc}:${kod}`]: !otvorena }))
            }
            className="flex items-center gap-1.5 text-left"
            aria-expanded={otvorena}
            aria-label={`Skupina ${nazov}`}
            title="Rozbaliť / zbaliť skupinu"
          >
            {otvorena ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            <span className="text-sm font-medium">{nazov}</span>
          </button>
          <div className="pl-6 text-xs text-muted-foreground">
            {formatPocet(pocet)} {sklonuj(pocet, POLOZKY_NOM)} ·{" "}
            {clenovia.length} {sklonuj(clenovia.length, ["kategória", "kategórie", "kategórií"])}
            {vlastnych > 0 ? ` · ${formatPocet(vlastnych)} s vlastnými maržami` : ""}
          </div>
        </td>
        {karta.polia.map((pole) => (
          <td key={pole} className="py-1.5 px-2 text-right align-top">
            {bunka(`${pole}|grp:${karta.kluc}:${kod}`, pole, clenovia, `Skupina ${nazov}`)}
          </td>
        ))}
      </tr>,
    ];
    if (otvorena) riadky.push(...clenovia.map((p) => riadok(p, karta, true)));
    return riadky;
  }

  if (query.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <p className="text-sm text-destructive">
        Chyba pri načítaní marží: {query.error?.message}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Práca, réžia a nadmiera každej kategórie. Klikni na číslo, uprav
        a stlač Enter — zmení sa kategória aj jej položky a import cenníka
        ho už nevráti. Položky s vlastnými maržami ostávajú; pri kovaní
        nastavíš číslo celej skupine naraz alebo ju rozklikni.
      </p>
      {karty.map((karta) => {
        const tovar = karta.kluc === "tovar";
        const smie = smieCennik(prava, tovar);
        return (
        <section
          key={karta.kluc}
          aria-label={karta.nazov}
          className="rounded-lg border bg-card p-4"
        >
          <fieldset disabled={!smie} className="contents">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-lg font-medium">
              {karta.nazov}{" "}
              <span className="text-sm text-muted-foreground">({karta.jednotka})</span>
            </h3>
            {smie ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSekcia(karta)}
                disabled={ulozit.isPending}
              >
                Nastaviť celej sekcii…
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">
                {tovar ? VETA.cennikTovaru : VETA.cennikVyroby}
              </span>
            )}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th className="px-2 py-1 text-left font-normal">Kategória</th>
                {karta.polia.map((pole) => (
                  <th key={pole} className="px-2 py-1 text-right font-normal">
                    {nazovPola(pole, karta)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {blokyKarty(karta).flatMap((b) => {
                if (b.typ === "nadpis") {
                  return [
                    <tr key={`h:${b.text}`}>
                      <td
                        colSpan={karta.polia.length + 1}
                        className="px-2 pt-3 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground"
                      >
                        {b.text}
                      </td>
                    </tr>,
                  ];
                }
                if (b.typ === "skupina") return skupina(b.kod, b.clenovia, karta);
                return [riadok(b.polozka, karta)];
              })}
            </tbody>
          </table>
          </fieldset>
        </section>
        );
      })}
      <MarzeSekciaDialog karta={sekcia} onClose={() => setSekcia(null)} />
    </div>
  );
}
